import {Injectable} from '@angular/core';
import {BaseProvider} from '../base.provider';
import {ObjectsService} from './objects.service';
import {ModelsInterfaces} from '@namespaces/models.namespace';

@Injectable()
export class ObjectsProvider extends BaseProvider<ObjectsData> {
    public defaultValue: ObjectsData = {
        raw: [],
        asTree: [],
        asRoot: [],
    };

    constructor(
        service: ObjectsService
    ) {
        super(service);
        /**
         * Устанавливаем дефолтные значения
         */
        this.data$.next(this.defaultValue);
        this.updateData();
    }

    /**
     * Получить номера всех подобъектов объекта, включая сам объект.
     * Для объекта 2 результат [2, 2.1, 2.1.1, 2.1.2, 2.2, 2.3]
     * @param object
     */
    static getRootAndChildsNumbers(object: ModelsInterfaces.Object): string[] {
        const numbers = [object.number];
        if (object.childs && object.childs.length) {
            object.childs.forEach(child => {
                numbers.push(...ObjectsProvider.getRootAndChildsNumbers(child));
            });
        }
        return numbers;
    }
    /**
     * Получить номера только всех конечных подобъектов объекта.
     * Для объекта 2 результат [2.1.1, 2.1.2, 2.2, 2.3], "Родительские" 2 и 2.1 не включены
     * @param object
     */
    static getOnlyChildsNumbers(object: ModelsInterfaces.Object): string[] {
        const numbers = [];
        if (object.childs && object.childs.length) {
            object.childs.forEach(child => {
                numbers.push(...ObjectsProvider.getOnlyChildsNumbers(child));
            });
        } else if (object.lvl > 2) { // только подобъекты
            numbers.push(object.number);
        }
        return numbers;
    }

    public processedData(data: ModelsInterfaces.Object[]): ObjectsData {
        if (!(data instanceof Array)) {
            data = [];
        }

        return {
            raw: data,
            asTree: this.makeTree(data),
            asRoot: this.getByParentId(1, data),
        };
    }

    public getRootByChildId(id: number, objects = this.data$.getValue().asTree): ModelsInterfaces.Object {
        let result = null;

        objects.some((obj) => {
            if (+obj.id === +id) {
                result = obj;
                return true;
            }

            if (obj.childs && obj.childs.length) {
                obj.childs.some(o => {
                    if (+o.id === +id) {
                        result = obj;
                        return true;
                    }
                });
            }

            return Boolean(result);
        });

        return result;
    }

    private makeTree(data: ModelsInterfaces.Object[]) {
        const root = this.getByParentId(1, data);

        return root.map(item => {
            return this.collectChildForItem(item, data);
        });
    }

    private collectChildForItem(item: ModelsInterfaces.Object, data: ModelsInterfaces.Object[]) {
        const {id} = item;
        const childs = this.getByParentId(id, data);

        const newItem: ModelsInterfaces.Object = {...item};

        if (childs && childs.length) {
            newItem.childs = childs.map(childItem => this.collectChildForItem(childItem, data));
        }

        return newItem;
    }

    private getByParentId(parentId: number, data: ModelsInterfaces.Object[]) {
        return data.filter(item => +item.parent_id === parentId);
    }

    public getById(id, objects = this.data$.getValue().asTree): ModelsInterfaces.Object {
        let result = null;

        objects.some(obj => {
            if (obj.id === id) {
                result = obj;
                return true;
            }

            if (obj.childs && obj.childs.length) {
                result = this.getById(id, obj.childs);
                return result;
            }
        });

        return result;
    }

    public getByNumber(number: string, objects = this.data$.getValue().asTree) {
        let result = null;

        objects.some(obj => {
            if (obj.number === number) {
                result = obj;
                return true;
            }

            if (obj.childs && obj.childs.length) {
                result = this.getByNumber(number, obj.childs);
                return result;
            }
        });

        return result;
    }

    /**
     * Получить root объект по его id или id любого его потомка
     * @param id
     * @param objects
     */
    public getRootById(id, objects = this.data$.getValue().asTree): ModelsInterfaces.Object {
        let result = null;

        objects.some(obj => {
            if (obj.id === id) {
                result = obj;
                return true;
            }

            if (obj.childs && obj.childs.length) {
                const has = this.getRootById(id, obj.childs);
                if (has) {
                    result = obj;
                    return true;
                }
            }
        });

        return result;
    }

    /**
     * Получить id объекта и всех его подобъектов
     * @param object
     */
    public getIdsForRootAndChild(object: ModelsInterfaces.Object): number[] {
        let ids: number[] = [object.id];

        if (object.childs && object.childs.length) {
            ids = ids.concat(...object.childs.map(obj => this.getIdsForRootAndChild(obj)));
        }

        return ids;
    }

    /**
     * Получить дочерний объект для текущего по id дочернего
     * @param id
     * @param object
     */
    public getChildById(id: number, object: ModelsInterfaces.Object): ModelsInterfaces.Object {
        let child = null;

        if (object.id === id) {
            return object;
        }

        if (object.childs && object.childs.length) {
            object.childs.some(ch => {
                child = this.getChildById(id, ch);
                return child;
            });
        }

        return child;
    }
}

export interface ObjectsData {
    raw: ModelsInterfaces.Object[];
    asRoot: ModelsInterfaces.Object[];
    asTree: ModelsInterfaces.Object[];
}
